import axios from "axios";
import { useMemo, useState } from "react";
import styles from "./EmailView.module.scss";
import { View, ViewHeader } from "@/components";
import { getApiErrorCode, handleAxiosError } from "@/helpers/axios";
import type {
  EmailMailboxFilter,
  EmailPeriodFilter,
  EmailToolbarTab,
} from "../../_constants/email-toolbar.const";
import { useEmailsQuery } from "../../_hooks/useEmailsQuery";
import { EmailToolbar } from "../email-toolbar";
import { EmailsList } from "../emails-list";

export const EmailView = () => {
  const [tab, setTab] = useState<EmailToolbarTab>("all");
  const [period, setPeriod] = useState<EmailPeriodFilter>("all");
  const [mailbox, setMailbox] = useState<EmailMailboxFilter>("all");

  const query = useEmailsQuery(tab, period, mailbox);
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  const gmailUnavailable = getApiErrorCode(query.error) === "gmail_not_configured";

  const errorMessage = useMemo(() => {
    if (!query.error) return null;
    if (gmailUnavailable) {
      return "Email inbox is unavailable because Gmail is not configured in this environment.";
    }
    if (axios.isAxiosError(query.error)) {
      const payload = handleAxiosError(query.error);
      return payload?.message ?? query.error.message;
    }
    return query.error instanceof Error ? query.error.message : "Something went wrong.";
  }, [query.error, gmailUnavailable]);

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading="Email"
          description="Handle inbox messages, review conversations, and keep email workflows organized.<br />Use this section to stay on top of email communication."
        />

        <EmailToolbar
          tab={tab}
          period={period}
          mailbox={mailbox}
          onTabChange={setTab}
          onPeriodChange={setPeriod}
          onMailboxChange={setMailbox}
          onComposeClick={() => undefined}
        />

        <EmailsList
          items={items}
          isLoading={query.isLoading}
          isFetching={query.isFetching}
          isError={query.isError}
          unavailable={gmailUnavailable}
          errorMessage={errorMessage}
          onRetry={() => query.refetch()}
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          fetchNextPage={() => query.fetchNextPage()}
        />
      </div>
    </View>
  );
};
